import { Attribute, Injectable } from "@angular/core";
import { Feature } from "app/bundles/requests/shared/models/feature";
import { IGeojsonRequest } from "app/bundles/requests/shared/models/geojsonRequest";
import { Properties } from "app/bundles/requests/shared/models/properties";
import { GeocoderService } from "app/bundles/requests/shared/services/geocoder.service";
import { IconService } from "app/bundles/requests/shared/services/icon.service";
import { UtilService } from "app/bundles/requests/shared/services/util.service";
import { CoreService, FilterListChange } from "app/core/core.service";
import { StorageService } from "app/shared/services/storage.service";
import { BOROUGH_CENTROIDS } from "environments/centroid.values.for.all.environment";
import { CurrentUserService } from "./current-user.service";
import "assets/js/leaflet-wmts";
import { environment } from "environments/environment";
import "leaflet";
import "@geoman-io/leaflet-geoman-free";
import { GeoJsonObject } from "geojson";
import { LatLngTuple } from "leaflet";
import "leaflet.markercluster";
import * as _ from "lodash-es";
import { combineLatest, filter, Observable, startWith } from "rxjs";
import {
  IWorkorderOrServiceRequestSummary,
  IWorkorderOrServiceRequestDetail,
} from "../models/request";
import { IBatchMode, RequestInBatchService } from "./request-in-batch.service";
import * as L from "leaflet";
import pointsWithinPolygon from "@turf/points-within-polygon";

@Injectable()
export class MapService {
  private map: L.Map = null;
  private arrayLatLngs: [number, number][] = [];
  private markerClicked: any = null;
  private geoJsonLayer: any;
  private markers: L.MarkerClusterGroup = null;
  private mapStatus: any = {};
  private locationGeoJsonLayer: any;
  private batchMode: IBatchMode;
  //
  // Because we cannot know if a zoom/move is triggered by the user or not.
  //
  // See: https://github.com/Leaflet/Leaflet/issues/2934
  //
  // Should be set to true before calling
  // something that cause a indirect move/zoom to occurs
  private _isMoveZoomProgrammatic = false;
  private buttonStartPolygonSelection;
  private containerForLargeMapButton;
  private currentUserBorough = null;

  constructor(
    private iconService: IconService,
    private coreService: CoreService,
    private utilService: UtilService,
    private storageService: StorageService,
    private geocoderService: GeocoderService,
    private requestInBatchService: RequestInBatchService,
    private currentUserService: CurrentUserService,
  ) {}

  buildStaticMap(request: IWorkorderOrServiceRequestDetail): void {
    if (
      request &&
      request.geometry &&
      request.geometry.coordinates &&
      request.geometry.coordinates.length === 2
    ) {
      const config: any = environment.mapConfig.wmts;
      const mapOptions: L.MapOptions = _.cloneDeep(config.mapOptions);

      mapOptions.minZoom = config.staticMap.staticMapZoom;
      mapOptions.maxZoom = config.staticMap.staticMapZoom;
      mapOptions.zoom = config.staticMap.staticMapZoom;
      mapOptions.scrollWheelZoom = config.staticMap.scrollWheelZoom;
      mapOptions.zoomControl = config.staticMap.zoomControl;
      mapOptions.dragging = config.staticMap.dragging;
      mapOptions.tap = false;
      mapOptions.touchZoom = false;
      mapOptions.boxZoom = false;
      mapOptions.doubleClickZoom = false;
      mapOptions.keyboard = false;

      mapOptions.crs = L.CRS.EPSG3857;
      this.map = L.map("staticMapId", mapOptions);
      const titleLayerOption: L.TileLayerOptions = config.titleLayerOption;
      (titleLayerOption as any).matrixIds = this.getMatrix();
      const layer = new L.TileLayer["WMTS"](config.url, titleLayerOption).addTo(
        this.map,
      );
      const bounds = L.latLngBounds(
        L.latLng(config.mapBound.southWest[0], config.mapBound.southWest[1]),
        L.latLng(config.mapBound.northEast[0], config.mapBound.northEast[1]),
      );

      this.map.setMaxBounds(bounds);
      this.map.setView(
        new L.LatLng(
          request.geometry.coordinates[1],
          request.geometry.coordinates[0],
        ),
        config.staticMap.staticMapZoom,
      );
      L.marker(
        new L.LatLng(
          request.geometry.coordinates[1],
          request.geometry.coordinates[0],
        ),
        { icon: this.getIconByRequest(request) },
      ).addTo(this.map);
    }
  }

  buildMap(): void {
    if (environment.currentMapType && environment.currentMapType === "WMTS") {
      this.buildMapWMTS();
    } else {
      this.buildMapOSM();
    }

    this.map.on("zoomstart movestart", (event: L.LeafletEvent) => {
      if (!this.isMoveZoomProgrammatic()) {
        // console.log(`startSpinner [${event.type}]`);
      } else {
        // console.log(`Je ne démarre pas le spinner car n'est pas déclenché par l'utilisateur [${event.type}]`);
      }
    });

    this.map.on("moveend zoomend", (event: L.LeafletEvent) => {
      if (!this.isMoveZoomProgrammatic()) {
        // console.log(`stopSpinner [${event.type}]`);
      } else {
        // console.log(`Je n'arrête pas le spinner car n'est pas déclenché par l'utilisateur [${event.type}]`);
      }
    });

    this.map.on("zoomend", () => {
      this.handleMapEvent();
    });

    this.map.on("moveend", () => {
      this.handleMapEvent();
    });

    this.coreService.requestSelectionFromList$.subscribe((res) => {
      const request: IWorkorderOrServiceRequestSummary = res;
      _.forEach(this.geoJsonLayer._layers, (marker) => {
        if (marker.feature && marker.feature.properties) {
          marker.feature.properties.isSelected =
            marker.feature.properties.id === request.id
              ? request.isSelected
              : false;
          marker.setIcon(this.getIcon(<Feature>marker.feature));
          marker.options.zIndexOffset = this.getZIndex(
            marker.feature.properties,
          );
        }
      });
      this.updateGeojson(this.storageService.getGeoJsonRequest());
      this.coreService.notifyRefreshIcons();
    });

    this.coreService.notifyObservable.subscribe((res) => {
      if (
        Object.prototype.hasOwnProperty.call(res, "option") &&
        res.option === "centerOnMarker"
      ) {
        this.centerOnMarker(res.value[1], res.value[0], 18);
      }
    });
    this.coreService.resetToDefaultZoom$.subscribe((res) => {
      if (res && this.map) {
        this.map.setZoom(environment.mapConfig.defaultZoom);
      }
    });

    this.coreService.updateMap$.subscribe((geoJsonRequest: IGeojsonRequest) => {
      this.updateGeojson(geoJsonRequest);
    });

    this.coreService.updateHandlerEvent$.subscribe(() => {
      this.handleMapEvent();
    });

    this.requestInBatchService.batchMode$.subscribe((batchMode: IBatchMode) => {
      if (this.batchMode && batchMode.isActive !== this.batchMode.isActive) {
        this.centerOnMarkers();
        this.updateGeojson(this.storageService.getGeoJsonRequest());
      }
      this.batchMode = batchMode;
    });

    this.setMapStatus();
  }

  public isMoveZoomProgrammatic(): boolean {
    return this._isMoveZoomProgrammatic;
  }

  public moveZoomProgrammatic(moveZoomProgrammatic: boolean) {
    this._isMoveZoomProgrammatic = moveZoomProgrammatic;
  }

  buildMapOSM(): void {
    this.map = new L.Map("mapid");
    const config: any = environment.mapConfig.osm;
    const urlTemplate = config.urlTemplate;
    const attribution = config.titleLayerOption.attribution;
    const titleLayer = new L.TileLayer(urlTemplate, {
      minZoom: config.titleLayerOption.minZoom,
      maxZoom: config.titleLayerOption.maxZoom,
      attribution: attribution,
    });
    this.map.setView(
      new L.LatLng(config.centerLatLng.lat, config.centerLatLng.Lng),
      config.zoom,
    );
    this.map.addLayer(titleLayer);
    const bounds = L.latLngBounds(
      L.latLng(
        config.titleLayerOption.bounds.southEast[0],
        config.titleLayerOption.bounds.southEast[1],
      ),
      L.latLng(
        config.titleLayerOption.bounds.northWest[0],
        config.titleLayerOption.bounds.northWest[1],
      ),
    );
    this.map.setMaxBounds(bounds);
  }

  buildMapWMTS(): void {
    const config: any = environment.mapConfig.wmts;
    const mapOptions = _.cloneDeep(config.mapOptions);
    mapOptions.staticMapZoom = 10;
    mapOptions.scrollWheelZoom = true;
    mapOptions.zoomControl = false;
    mapOptions.dragging = true;
    const currentUserBoroughId = this.currentUserService.getUserBoroughId();
    if (currentUserBoroughId) {
      const borough = BOROUGH_CENTROIDS.find(
        (borough) => borough.code === currentUserBoroughId,
      );
      if (borough) {
        mapOptions.center = borough.coordinates;
        mapOptions.zoom = borough.zoom;
        this.currentUserBorough = borough;
      }
    }

    mapOptions.crs = L.CRS.EPSG3857;
    this.map = L.map("mapid", mapOptions);
    this.map.pm.setLang("fr");
    const titleLayerOption = config.titleLayerOption;
    titleLayerOption.matrixIds = this.getMatrix();
    const layer: L.TileLayer = new L.TileLayer["WMTS"](
      config.url,
      titleLayerOption,
    ).addTo(this.map);
    this.map.on("pm:create", (event) => {
      const polygon = event.layer as L.Polygon;
      const geojsonWithMarker: GeoJSON.Feature<GeoJSON.Point, any>[] =
        this.markers
          .getLayers() // getAllChildMarkers not used here since not implemented in MarkerClusterGroup (bad typing)
          .map((m: L.Marker) => {
            const r = m.toGeoJSON();
            r.properties.marker = m;
            return r;
          });

      // console.log(`J'effectue une recherche spatiale sur ${geojsonWithMarker.length} demande(s)`);
      const ptsWithin = pointsWithinPolygon(
        { features: geojsonWithMarker, type: "FeatureCollection" },
        polygon.toGeoJSON(),
      );
      let cpt = 0;
      ptsWithin.features.forEach((feature) => {
        const marker = feature.properties.marker;
        const e = { target: marker };
        if (!this.isFeatureDisabled(feature)) {
          this.onSelectInBatch(e, feature);
          cpt++;
          // console.log(feature.properties.requestNumber);
        }

        this.coreService.notifyZoomMarker(e.target.feature.properties);
        if (feature.properties.id) {
          this.coreService.notifyScrollToRequest(feature);
        }
        this.coreService.notifyRefreshIcons();

        if (this.markerClicked) {
          this.markerClicked.unbindPopup();
        }

        if (this.markerClicked === marker) {
          this.clearPopup();
          this.markerClicked = null;
          return;
        }

        this.markerClicked = marker;
        this.coreService.notifyRequestIconClicked(e.target.feature.properties);
      });
      // console.log(`${cpt} demandes sélectionées`);
      this.addStartPolygonSelectionButton();
      // No need to update Geojson since no changes has been made except for selection
      // this.updateGeojson(this.storageService.getGeoJsonRequest())
      setTimeout(() => event.layer.remove(), 2000);
    });
    const bounds = L.latLngBounds(
      L.latLng(config.mapBound.southWest[0], config.mapBound.southWest[1]),
      L.latLng(config.mapBound.northEast[0], config.mapBound.northEast[1]),
    );

    this.map.setMaxBounds(bounds);
    this.map.on("drag", () => {
      this.map.panInsideBounds(bounds, { animate: false });
    });

    L.control
      .scale({
        imperial: false,
      })
      .addTo(this.map);

    const MapControl = L.Control.extend({
      options: {
        position: "topright",
      },

      onAdd: (map) => {
        const container = L.DomUtil.create("div", "div-leaflet");

        const buttonRefresh = this.createMapButton(
          "btn btn-light rounded btn-leaflet-spacing",
          "fa fa-refresh",
        );
        const buttonLocation = this.createMapButton(
          "btn btn-light rounded btn-leaflet-spacing",
          "fa fa-crosshairs",
        );
        const buttonPlus = this.createMapButton(
          "btn btn-light rounded",
          "fa fa-plus",
        );
        const buttonMinus = this.createMapButton(
          "btn btn-light rounded",
          "fa fa-minus",
        );

        buttonRefresh.addEventListener("click", () => {
          this.setHomeZoom();
        });

        buttonLocation.addEventListener("click", () => {
          this.getCurrentLocation();
        });

        buttonPlus.addEventListener("click", () => {
          this.doZoomIn();
        });

        buttonMinus.addEventListener("click", () => {
          this.doZoomOut();
        });

        container.appendChild(buttonRefresh);
        container.appendChild(buttonLocation);
        container.appendChild(buttonPlus);
        container.appendChild(buttonMinus);

        return container;
      },
    });

    const MapLargeControl = L.Control.extend({
      options: {
        position: "bottomright",
      },

      onAdd: (map) => {
        const containerForLargeMapButton = L.DomUtil.create(
          "div",
          "div-leaflet",
        );
        this.buttonStartPolygonSelection = this.createLargeMapButton(
          "btn btn-light",
          "fa fa-check-circle",
          "Tracer un polygone",
        );
        this.containerForLargeMapButton = containerForLargeMapButton;

        this.buttonStartPolygonSelection.addEventListener("click", (e) => {
          e.stopPropagation();
          this.startPolygonSelection();
        });

        this.manageStartPolygonSelectionAvailability();

        containerForLargeMapButton.appendChild(
          this.buttonStartPolygonSelection,
        );

        return containerForLargeMapButton;
      },
    });

    const mapControl = new MapControl();
    mapControl.addTo(this.map);

    const mapLargeControl = new MapLargeControl();
    mapLargeControl.addTo(this.map);

    this.getCurrentLocation(false);
  }

  private manageStartPolygonSelectionAvailability() {
    this.disablePolygonSelectionButton(this.buttonStartPolygonSelection);

    combineLatest([
      this.requestInBatchService.batchMode$,
      this.coreService.filterListChange$.pipe(
        // Handle case when search filter criteria are restored from local storage on app startup
        startWith({
          filterType: "serviceCode",
          value: this.storageService.getFilters().serviceCode,
        }),
        filter((c) => c.filterType === "serviceCode"),
      ),
    ]).subscribe(([batchMode, serviceChange]: [IBatchMode, FilterListChange]) =>
      this.handleBatchModeServiceChange(batchMode, serviceChange),
    );

    this.coreService.resetFilter$.subscribe((v) =>
      this.disablePolygonSelectionButton(this.buttonStartPolygonSelection),
    );
  }

  private handleBatchModeServiceChange(
    batchMode: IBatchMode,
    serviceChange: FilterListChange,
  ): void {
    if (batchMode.isActive && serviceChange.value) {
      this.enablePolygonSelectionButton(this.buttonStartPolygonSelection);
      return;
    }
    this.disablePolygonSelectionButton(this.buttonStartPolygonSelection);
  }

  private startPolygonSelection() {
    this.map.pm.enableDraw("Polygon", {
      allowSelfIntersection: false,
      finishOn: "dblclick",
    });

    this.removeStartPolygonSelectionButton();
  }

  private removeStartPolygonSelectionButton() {
    this.containerForLargeMapButton.removeChild(
      this.buttonStartPolygonSelection,
    );
  }

  private addStartPolygonSelectionButton() {
    this.containerForLargeMapButton.appendChild(
      this.buttonStartPolygonSelection,
    );
  }

  private doZoomIn() {
    this.map.zoomIn();
  }

  private doZoomOut() {
    this.map.zoomOut();
  }

  createMapButton(className: string, iconName: string) {
    const button = L.DomUtil.create("button", className);
    const span = L.DomUtil.create("span", iconName);
    button.appendChild(span);
    return button;
  }

  createLargeMapButton(
    className: string,
    iconName: string,
    textContent: string,
  ) {
    const button = L.DomUtil.create("button", className);
    const span = L.DomUtil.create("span", `${iconName} align-bottom`);
    const spanTextContent = L.DomUtil.create("span", "ml-2");
    spanTextContent.textContent = textContent;
    button.appendChild(span);
    button.appendChild(spanTextContent);
    return button;
  }
  updateGeojson(geojesonData: IGeojsonRequest): void {
    if (this.markers) {
      this.markers.removeLayer(this.geoJsonLayer);
      this.map.removeLayer(this.markers);
    }
    this.markers = L.markerClusterGroup({
      chunkedLoading: true,
      removeOutsideVisibleBounds: true,
      spiderfyOnMaxZoom: false,
      maxClusterRadius: 120,
      disableClusteringAtZoom:
        environment.mapConfig.wmts.mapOptions.popBubblesAtZoom,
      iconCreateFunction: (cluster) => {
        return L.divIcon({
          html: `<b>${cluster.getChildCount()}</b>`,
          className: "cluster-icon",
          iconSize: L.point(30, 30),
        });
      },
    });

    this.arrayLatLngs = [];
    this.geoJsonLayer = L.geoJSON(geojesonData as GeoJsonObject, {
      filter: (feature: any) => {
        return feature.properties.isFiltred;
      },
      pointToLayer: (feature: any, latlng) => {
        const isFeatureDisabled = this.isFeatureDisabled(feature);
        const markerIcon = this.getIcon(feature);
        const marker = L.marker(latlng, {
          icon: markerIcon,
          zIndexOffset: this.getZIndex(feature.properties),
          opacity: this.batchMode.isActive && isFeatureDisabled ? 0.2 : 1,
        });

        marker.on("click", (e) => {
          // console.log(`startSpinner`);
          if (this.batchMode.isActive) {
            if (!isFeatureDisabled) {
              this.onSelectInBatch(e, feature);
              this.updateGeojson(geojesonData);
            }
          } else {
            const selectedFeature: Feature = <Feature>e.target.feature;
            const isSelected: boolean = selectedFeature.properties.isSelected;
            this.removeRequestsSelection(selectedFeature);
            e.target.feature.properties.isSelected = !isSelected;
            this.utilService.updateSelectionFeature(
              this.storageService.getGeoJsonRequest(),
              e.target.feature.properties,
            );
            e.target.setIcon(this.getIcon(e.target.feature));
            e.target.options.zIndexOffset = this.getZIndex(
              e.target.feature.properties,
            );
          }

          // this.handleMapEvent();
          this.coreService.notifyZoomMarker(e.target.feature.properties);
          if (feature.properties.id) {
            this.coreService.notifyScrollToRequest(feature);
          }
          this.coreService.notifyRefreshIcons();

          if (this.markerClicked) {
            this.markerClicked.unbindPopup();
          }

          if (this.markerClicked === marker) {
            this.clearPopup();
            this.markerClicked = null;
            return;
          }

          this.markerClicked = marker;
          this.coreService.notifyRequestIconClicked(
            e.target.feature.properties,
          );
        });
        return marker;
      },
    });

    this.markers.addLayer(this.geoJsonLayer);
    this.map.addLayer(this.markers);

    const bounds: any = new L.LatLngBounds(this.arrayLatLngs);
    if (bounds && bounds.length > 0) this.map.fitBounds(bounds);
  }

  private isFeatureDisabled(feature: any): boolean {
    return this.requestInBatchService.isRequestInBatchDisabled(
      this.utilService.getRequestFromProperties(feature),
    );
  }

  private onSelectInBatch(e: any, feature: any) {
    const selectedFeature: Feature = <Feature>e.target.feature;
    const isSelected: boolean = selectedFeature.properties.isSelected;
    if (!isSelected) {
      this.requestInBatchService.addSelectedRequest(
        this.utilService.getRequestFromProperties(feature),
      );
    } else {
      this.requestInBatchService.removeSelectedRequest(
        this.utilService.getRequestFromProperties(feature),
      );
    }
    e.target.feature.properties.isSelected = !isSelected;
    e.target.setIcon(this.getIcon(e.target.feature));
    e.target.options.zIndexOffset = this.getZIndex(e.target.feature.properties);
  }

  updateLocationGeojson(geojesonData: any): void {
    this.removeLocationLayers();
    this.locationGeoJsonLayer = L.geoJSON(geojesonData, {
      pointToLayer: (feature: any, latlng) => {
        const markerIcon = this.getLocationIcon();
        const marker = L.marker(latlng, {
          icon: markerIcon,
          zIndexOffset: 3000,
        });
        return marker;
      },
    }).addTo(this.map);
  }

  getLocationIcon(): any {
    return L.icon({
      iconUrl: "assets/images/map-icons/geo-icon.svg",
      iconSize: [30, 30],
    });
  }

  getZIndex(properties: Properties): number {
    return properties.isSelected ? 3000 : 2000;
  }

  getIcon(feature: Feature): any {
    const request: IWorkorderOrServiceRequestSummary =
      this.utilService.getRequestFromProperties(feature);
    const icon = L.icon({
      iconUrl:
        "assets/images/map-icons/" + this.iconService.getImageName(request),
      iconSize: [30, 30],
    });
    return icon;
  }

  getIconByRequest(request: IWorkorderOrServiceRequestDetail): any {
    const icon = L.icon({
      iconUrl:
        "assets/images/map-icons/" +
        this.iconService.getImageName(request.requestSummary),
      iconSize: [30, 30],
    });
    return icon;
  }

  refreshMap() {
    setTimeout(() => {
      this.map.invalidateSize(true);
    }, 5);
  }

  centerOnMarker(lat: number, lng: number, zoom: number) {
    this.map.setZoom(zoom);
    setTimeout(() => {
      this.map.panTo(new L.LatLng(lat, lng), { animate: true, duration: 0.5 });
    }, 500);
  }

  centerOnMarkers(): void {
    // console.log(`startSpinner`);
    if (this.arrayLatLngs.length == 0) {
      const bounds = new L.LatLngBounds([
        [
          environment.mapConfig.centerLatLng.lat,
          environment.mapConfig.centerLatLng.Lng,
        ],
      ]);
      this.map.fitBounds(bounds, { maxZoom: 10.45 });
    } else {
      const bounds = new L.LatLngBounds(this.arrayLatLngs);
      if (bounds) {
        this.map.fitBounds(bounds);
      }
    }
    // console.log(`stopSpinner`);
  }

  public showPopup(element: any): void {
    if (this.markerClicked && element) {
      this.markerClicked.bindPopup(element, { maxWidth: 500 }).openPopup();
    }
  }

  public clearPopup(): void {
    if (this.map) {
      this.map.closePopup();
    }
  }

  removeLayers(): void {
    this.map.eachLayer((layer: any) => {
      if (layer.feature) {
        this.map.removeLayer(layer);
      }
    });
  }

  removeLocationLayers(): void {
    this.map.eachLayer((layer: any) => {
      if (layer.feature) {
        if (
          layer.feature.properties &&
          layer.feature.properties.iconType &&
          layer.feature.properties.iconType === "geolocalization"
        ) {
          this.map.removeLayer(layer);
        }
      }
    });
  }

  removeRequestsSelection(selectedFeature: Feature): void {
    _.forEach(this.geoJsonLayer._layers, (marker) => {
      if (marker.feature && marker.feature.properties) {
        marker.feature.properties.isSelected = false;
        marker.setIcon(this.getIcon(<Feature>marker.feature));
        marker.options.zIndexOffset = 2000;
        this.coreService.notifyZoomMarker(marker.feature.properties);
      }
    });
  }

  getBoundIds(): number[] {
    const ids: number[] = [];
    const geoJsonRequest: IGeojsonRequest =
      this.storageService.getGeoJsonRequest();
    const bounds: any = this.map.getBounds();
    if (geoJsonRequest) {
      _.forEach(geoJsonRequest.features, (value, key) => {
        if (value.properties.isFiltred) {
          const coord: any = new L.LatLng(
            value.geometry.coordinates[1],
            value.geometry.coordinates[0],
          );
          if (bounds.contains(coord)) {
            ids.push(value.properties.id);
          }
        }
      });
    }
    return ids;
  }

  handleMapEvent(): void {
    const ids = this.getBoundIds();
    this.coreService.notifyRequestUpdate(ids);
    setTimeout(() => {
      const feature: Feature = this.findSelectedFeature();
      if (feature) {
        this.coreService.notifyScrollToRequest(feature);
      }
    }, 5);
  }

  getMatrix(): any[] {
    return [
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:0`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 0,
        minTileRow: 0,
        maxTileRow: 0,
        minTileCol: 0,
        maxTileCol: 0,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:1`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 1,
        minTileRow: 0,
        maxTileRow: 0,
        minTileCol: 0,
        maxTileCol: 0,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:2`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 2,
        minTileRow: 1,
        maxTileRow: 1,
        minTileCol: 1,
        maxTileCol: 1,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:3`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 3,
        minTileRow: 2,
        maxTileRow: 2,
        minTileCol: 2,
        maxTileCol: 2,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:4`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 4,
        minTileRow: 5,
        maxTileRow: 5,
        minTileCol: 4,
        maxTileCol: 4,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:5`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 5,
        minTileRow: 11,
        maxTileRow: 11,
        minTileCol: 9,
        maxTileCol: 9,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:6`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 6,
        minTileRow: 22,
        maxTileRow: 22,
        minTileCol: 18,
        maxTileCol: 18,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:7`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 7,
        minTileRow: 45,
        maxTileRow: 45,
        minTileCol: 37,
        maxTileCol: 37,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:8`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 8,
        minTileRow: 91,
        maxTileRow: 91,
        minTileCol: 75,
        maxTileCol: 75,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:9`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 9,
        minTileRow: 18,
        maxTileRow: 183,
        minTileCol: 150,
        maxTileCol: 151,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:10`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 10,
        minTileRow: 364,
        maxTileRow: 367,
        minTileCol: 300,
        maxTileCol: 303,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:11`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 11,
        minTileRow: 729,
        maxTileRow: 735,
        minTileCol: 601,
        maxTileCol: 606,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:12`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 12,
        minTileRow: 1459,
        maxTileRow: 1470,
        minTileCol: 1202,
        maxTileCol: 1213,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:13`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 13,
        minTileRow: 2919,
        maxTileRow: 2940,
        minTileCol: 2405,
        maxTileCol: 2427,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:14`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 14,
        minTileRow: 5839,
        maxTileRow: 5881,
        minTileCol: 4811,
        maxTileCol: 4855,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:15`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 15,
        minTileRow: 11679,
        maxTileRow: 11762,
        minTileCol: 9623,
        maxTileCol: 9711,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:16`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 16,
        minTileRow: 23358,
        maxTileRow: 23524,
        minTileCol: 19246,
        maxTileCol: 19423,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:17`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 17,
        minTileRow: 46717,
        maxTileRow: 47048,
        minTileCol: 38492,
        maxTileCol: 38846,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:18`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 18,
        minTileRow: 93435,
        maxTileRow: 94096,
        minTileCol: 76985,
        maxTileCol: 77692,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:19`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 19,
        minTileRow: 186871,
        maxTileRow: 188192,
        minTileCol: 153970,
        maxTileCol: 155385,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:20`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 20,
        minTileRow: 373743,
        maxTileRow: 376384,
        minTileCol: 307940,
        maxTileCol: 310771,
      },
      {
        identifier: `${environment.mapConfig.wmts.titleLayerOption.tilematrixSet}:21`,
        topLeftCorner: new L.LatLng(
          2.0037508342789244e7,
          -2.0037508342789244e7,
        ),
        zoom: 21,
        minTileRow: 747487,
        maxTileRow: 752768,
        minTileCol: 615880,
        maxTileCol: 621543,
      },
    ];
  }

  findSelectedFeature(): Feature {
    const marker: any = _.find(this.geoJsonLayer._layers, (marker) => {
      return marker.feature.properties.isSelected === true;
    });
    return marker ? marker.feature : null;
  }

  saveMapStatus(): void {
    if (this.map && this.map.getBounds()) {
      _.set(this.mapStatus, "currentBounds", this.map.getBounds());
    }

    if (this.map) {
      const currentCenter: any = this.map.getCenter();
      const currentZoom: number = this.map.getZoom();
      _.set(this.mapStatus, "currentCenter", [
        currentCenter.lat,
        currentCenter.lng,
      ]);
      _.set(this.mapStatus, "currentZoom", currentZoom);
    }
  }

  setMapStatus(): void {
    if (this.mapStatus && this.mapStatus.currentBounds) {
      const center = this.mapStatus.currentBounds.getCenter();
      if (center) {
        this.centerOnMarker(center.lat, center.lng, this.mapStatus.currentZoom);
      }
      setTimeout(() => {
        const feature: Feature = this.findSelectedFeature();
        this.coreService.notifyScrollToRequest(feature);
      }, 5);
    }
  }

  resetMapStatus(): Observable<any> {
    return new Observable((observer) => {
      _.set(this.mapStatus, "currentBounds", undefined);
      _.set(this.mapStatus, "currentCenter", undefined);
      _.set(this.mapStatus, "currentZoom", undefined);
      observer.next();
      observer.complete();
    });
  }

  verifyMap(msg?: string): void {
    const ids = this.getBoundIds();
    _.forEach(this.geoJsonLayer._layers, (marker) => {
      if (marker && marker.feature) {
        for (const x of ids) {
          if (x === marker.feature.properties.id) {
            marker.options.zIndexOffset = this.getZIndex(
              marker.feature.properties,
            );
          }
        }
      }
    });
  }

  getCurrentLocation(needZoom = true): void {
    this.geocoderService.getCurrentLocation().subscribe(
      (data) => {
        if (needZoom) {
          // console.log(`startSpinner`);
          this.moveZoomProgrammatic(true);

          setTimeout(() => {
            this.map.setView(
              new L.LatLng(data.latitude, data.longitude),
              environment.mapConfig.wmts.geolocalizationZoom,
            );

            this.moveZoomProgrammatic(false);
            // console.log(`stopSpinner`);
          }, 50);
        }
        this.updateLocationGeojson(
          this.getLocationGeojson(data.latitude, data.longitude),
        );
      },
      (error) => {
        // console.log(`stopSpinnerErr`);
      },
    );
  }

  setHomeZoom() {
    // console.log(`startSpinner`);
    this.moveZoomProgrammatic(true);
    this.map.setView(
      <LatLngTuple>environment.mapConfig.wmts.mapOptions.center,
      environment.mapConfig.wmts.mapOptions.minZoom,
    );
    this.moveZoomProgrammatic(false);
    // console.log(`stopSpinner`);
  }

  setUserZoom() {
    if (this.currentUserBorough) {
      this.moveZoomProgrammatic(true);
      this.map.setView(
        this.currentUserBorough.coordinates,
        this.currentUserBorough.zoom,
      );
      this.moveZoomProgrammatic(false);
    } else {
      this.setHomeZoom();
    }
  }

  getLocationGeojson(lat: number, lng: number) {
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
          properties: {
            iconType: "geolocalization",
          },
        },
      ],
    };
  }

  enablePolygonSelectionButton(buttonPolygonSelection: HTMLElement) {
    buttonPolygonSelection.removeAttribute("disabled");
  }

  disablePolygonSelectionButton(buttonPolygonSelection: HTMLElement) {
    buttonPolygonSelection.setAttribute("disabled", "");
  }
}
